import { useKeycloak } from '@react-keycloak/web'
import FileSaver from 'file-saver'
import { Dispatch, SetStateAction, useCallback } from 'react'
import { toast } from 'react-toastify'

import { appConfig } from '@/config/appConfig'
import { useAppContext } from '@/hooks'
import { useTaskMonitor } from '@/hooks/useTaskMonitor'
import { useConfirmationModal } from '@/pages/App/components/ConfirmationService'
import { ConfirmationOptions } from '@/pages/App/types'
import {
	MassExportExternalConfigDto,
	useGenerateScriptsMutation,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { logError, logErrorAndNotifyWithToast } from '@/utils/errorHandling'

const getFileMetadata = (
	massExportStrategy: MassExportExternalConfigDto['massExportStrategy'],
) => {
	const isSingleFile = massExportStrategy === 'SINGLE_FILE'

	return {
		fileName: isSingleFile ? 'export.txt' : 'export.zip',
		type: isSingleFile ? 'application/text' : 'application/zip',
	}
}

export const useAsyncMassExportV2 = () => {
	const { t } = useAppContext()
	const { keycloak } = useKeycloak()
	const { openConfirmModal, closeConfirmModal } = useConfirmationModal()
	const [generateScript] = useGenerateScriptsMutation()
	const { monitorTask, stopMonitoring } = useTaskMonitor()

	const executeAsyncMassExport = useCallback(
		async (
			massExportConfigDto: MassExportExternalConfigDto,
			setExporting: Dispatch<SetStateAction<boolean>>,
		) => {
			const massExportStrategy =
				massExportConfigDto.massExportStrategy || 'SINGLE_FILE'

			const elementIds =
				massExportConfigDto.structureIds?.join(', ') || 'unknown'

			const downloadFile = async (taskIdToDownload: number) => {
				try {
					const token = keycloak.token

					if (!token) {
						logError(new Error('No auth token available'), {
							operation: 'massExport.downloadFile',
							details: { taskId: taskIdToDownload },
						})
						toast(t('TASK_ERROR'), { type: 'error' })
						return
					}

					const response = await fetch(
						`${appConfig.apiUrl}/task/${taskIdToDownload}/bin`,
						{
							headers: {
								Authorization: `Bearer ${token}`,
								'X-TenantId': appConfig.realm,
							},
						},
					)

					if (!response.ok) {
						if (response.status === 404) {
							const errorMessage = t('EXPORT_NOT_AVAILABLE', [elementIds])
							logError(
								new Error(`Export not available for elements: ${elementIds}`),
								{
									operation: 'massExport.downloadFile.unauthorized',
									details: {
										taskId: taskIdToDownload,
										status: response.status,
										elementIds,
									},
								},
							)
							toast(errorMessage, { type: 'error' })
							return
						}

						logError(
							new Error(`HTTP ${response.status}: ${response.statusText}`),
							{
								operation: 'massExport.downloadFile.fetch',
								details: {
									taskId: taskIdToDownload,
									status: response.status,
									statusText: response.statusText,
								},
							},
						)
						toast(t('TASK_ERROR'), { type: 'error' })
						return
					}

					const arrayBuffer = await response.arrayBuffer()

					if (!arrayBuffer || arrayBuffer.byteLength === 0) {
						logError(new Error('Downloaded file is empty'), {
							operation: 'massExport.downloadFile.emptyFile',
							details: { taskId: taskIdToDownload },
						})
						toast(t('TASK_ERROR'), { type: 'error' })
						return
					}

					const { fileName, type } = getFileMetadata(massExportStrategy)
					const blob = new Blob([new Uint8Array(arrayBuffer)], { type })

					FileSaver.saveAs(blob, fileName)
				} catch (error) {
					logErrorAndNotifyWithToast({
						error,
						context: {
							operation: 'massExport.downloadFile',
							details: { taskId: taskIdToDownload },
						},
						t,
					})
				}
			}

			const createProgressUpdateHandler = (currentTaskId: number) => {
				return async (
					options: ConfirmationOptions,
				): Promise<ConfirmationOptions | null> => {
					const isComplete = options.taskState === 'FINISHED'
					try {
						await openConfirmModal({
							...options,
							autoStart: true,
							preserveProcessingState: true,
							showDownloadButton: isComplete,
							catchOnCancel: true,
							onDownload: isComplete
								? async () => {
										await downloadFile(currentTaskId)
										closeConfirmModal()
										setExporting(false)
									}
								: undefined,
						})
					} catch (error) {
						logError(error, {
							operation: 'massExport.updateModal',
							details: { taskId: currentTaskId, progress: options.progress },
						})
						stopMonitoring()
						setExporting(false)
					}
					return null
				}
			}

			const handleTaskCompletion = async (_completedTaskId: number) => {
				// no-op: download handled via modal's onDownload button
			}

			try {
				const response = await generateScript({
					massExportExternalConfigDto: massExportConfigDto,
				}).unwrap()

				if (!response?.id) {
					logError(new Error('Invalid task response: missing id'), {
						operation: 'massExport.createTask',
						details: { response },
					})
					toast(t('TASK_ERROR'), { type: 'error' })
					setExporting(false)
					return
				}

				const taskId = response.id

				await openConfirmModal({
					title: t('MASS_DOWNLOAD'),
					description: t('TASK_RUNNING_WAIT'),
					isAsync: true,
					autoStart: true,
					progress: 0,
					showDownloadButton: true,
					onDownload: undefined,
				})

				await monitorTask(taskId, {
					onSuccess: handleTaskCompletion,
					onProgressUpdate: createProgressUpdateHandler(taskId),
					onError: (error, detail) => {
						logErrorAndNotifyWithToast({
							error,
							context: {
								operation: 'massExport.monitorTask',
								details: { taskId },
							},
							t,
							detail,
						})
						stopMonitoring()
						setExporting(false)
					},
				})
			} catch (error) {
				logErrorAndNotifyWithToast({
					error,
					context: {
						operation: 'massExport.execute',
						details: { massExportConfigDto },
					},
					t,
				})
				openConfirmModal({
					title: t('MASS_DOWNLOAD'),
					description: t('TASK_ERROR'),
					isAsync: true,
					autoStart: true,
					preserveProcessingState: true,
					hasError: true,
				})
				setExporting(false)
			}
		},
		[
			keycloak,
			openConfirmModal,
			closeConfirmModal,
			t,
			generateScript,
			monitorTask,
			stopMonitoring,
		],
	)

	return { executeAsyncMassExport }
}
